import { useState } from 'react'

import axios from 'axios'
import styled from 'styled-components'

import { integrationBranding } from '../../../config/integrationBranding'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useUserContext } from '../../../contexts/userContext'

const { unifier: unifierBranding } = integrationBranding

interface SendToUnifierButtonProps {
  entityLabel: string
  onSend: () => Promise<void>
  compact?: boolean
  stylePreset?:
    | 'default'
    | 'progressPanel'
    | 'safetyDetail'
    /** Same 36px row height as adjacent `StandardButton` (Reports Delete). */
    | 'reportsToolbar'
  /** When false, hide the button (project has no Unifier integration). */
  unifierIntegrationActive?: boolean
}

export const SendToUnifierButton = ({
  entityLabel,
  onSend,
  compact = false,
  stylePreset = 'default',
  unifierIntegrationActive = false,
}: SendToUnifierButtonProps) => {
  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()

  const [showConfirm, setShowConfirm] = useState(false)
  const [isSending, setIsSending] = useState(false)

  if (!unifierIntegrationActive) return null
  if (userState.user_type !== 'super') return null

  const handleConfirm = async () => {
    setIsSending(true)
    try {
      await onSend()
      addNotification(unifierBranding.successToast(entityLabel), 'success')
      setShowConfirm(false)
    } catch (err: unknown) {
      let detail = unifierBranding.defaultFailureToast(
        entityLabel.toLowerCase(),
      )
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          | { message?: string; error?: string }
          | undefined
        const m = data?.message ?? data?.error
        if (typeof m === 'string' && m.trim()) {
          detail = m.trim()
        }
      }
      addNotification(detail, 'error')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      <UnifierButton
        compact={compact}
        stylePreset={stylePreset}
        onClick={() => setShowConfirm(true)}
      >
        {unifierBranding.sendButtonLabel}
      </UnifierButton>

      {showConfirm && (
        <ModalOverlay onClick={() => !isSending && setShowConfirm(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>{unifierBranding.modalTitle}</ModalHeader>
            <ModalBody>
              <p>
                Are you sure you want to send this{' '}
                <strong>{entityLabel.toLowerCase()}</strong> to{' '}
                {unifierBranding.confirmDestinationName}?
              </p>
              <ModalSecondaryText>
                This queues the data for export. You will be notified of sync
                status.
              </ModalSecondaryText>
            </ModalBody>
            <ModalFooter>
              <CancelButton
                onClick={() => setShowConfirm(false)}
                disabled={isSending}
              >
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleConfirm} disabled={isSending}>
                {isSending ? 'Sending...' : 'Confirm & Send'}
              </ConfirmButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

const UnifierButton = styled.button<{
  compact?: boolean
  stylePreset?: 'default' | 'progressPanel' | 'safetyDetail' | 'reportsToolbar'
}>`
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${props => {
    if (props.stylePreset === 'reportsToolbar') return '6px 12px'
    if (props.stylePreset === 'progressPanel') return '5px 10px'
    if (props.stylePreset === 'safetyDetail') return '10px 10px'
    return props.compact ? '4px 10px' : '8px 16px'
  }};
  font-size: ${props => {
    if (props.stylePreset === 'reportsToolbar') return 'inherit'
    if (props.stylePreset === 'progressPanel') return '0.9em'
    if (props.stylePreset === 'safetyDetail') return '0.9em'
    return props.compact ? '0.8em' : '0.9em'
  }};
  background: #073c7a;
  color: #fff;
  border: none;
  border-radius: ${props => (props.stylePreset === 'default' ? '0' : '2px')};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  margin: ${props => (props.stylePreset === 'safetyDetail' ? '5px' : '0')};
  flex-shrink: ${props => (props.stylePreset === 'reportsToolbar' ? 0 : 1)};
  min-height: ${props => {
    if (props.stylePreset === 'reportsToolbar') return '36px'
    if (props.stylePreset === 'progressPanel') return '28px'
    return 'auto'
  }};
  height: ${props => {
    if (props.stylePreset === 'reportsToolbar') return '36px'
    if (props.stylePreset === 'progressPanel') return '28px'
    return 'auto'
  }};
  box-sizing: border-box;

  &:hover {
    background: #0a4fa0;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 440px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`

const ModalHeader = styled.div`
  padding: 16px 20px;
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #ecf0f1;
`

const ModalBody = styled.div`
  padding: 20px;
  color: #333;
  line-height: 1.5;
`

const ModalSecondaryText = styled.p`
  font-size: 0.85em;
  color: #666;
  margin-top: 8px;
`

const ModalFooter = styled.div`
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #ecf0f1;
`

const CancelButton = styled.button`
  padding: 8px 16px;
  font-size: 0.9em;
  background: #ecf0f1;
  color: #333;
  border: none;
  border-radius: 0;
  cursor: pointer;

  &:hover {
    background: #dce0e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ConfirmButton = styled.button`
  padding: 8px 16px;
  font-size: 0.9em;
  background: #073c7a;
  color: #fff;
  border: none;
  border-radius: 0;
  cursor: pointer;

  &:hover {
    background: #0a4fa0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
